import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowRight, LayoutGrid, Lock } from 'lucide-react';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsRow, SettingsSaveBar, SettingsSection, Switch } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { settingsService } from '@/services/settings.service';
import { CRM_PAGES, LABEL_DEFAULTS, LabelKey, PageKey } from '@/lib/crm-pages';
import { industryIcon, industryOption } from '@/lib/industries';

/** Words worth listing — the Operations group label and unused pages stay out. */
const WORD_KEYS: LabelKey[] = ['leads', 'contacts', 'deals', 'appointments', 'services', 'bookings', 'catalog', 'orders', 'tickets', 'reservations', 'staff', 'stock', 'projects', 'retainers'];
const WORD_PAGE: Partial<Record<LabelKey, PageKey>> = {
  leads: 'leads', contacts: 'contacts', deals: 'deals', appointments: 'appointments', services: 'appointment_services',
  bookings: 'bookings', catalog: 'catalog', orders: 'orders', tickets: 'tickets', reservations: 'reservations',
  staff: 'staff', stock: 'stock', projects: 'projects', retainers: 'retainers',
};

const HEADER = {
  eyebrow: 'Workspace',
  title: 'Industry & pages',
  description: 'Your industry decides the setup your CRM starts with. You choose which pages your team sees.',
};

/**
 * Settings → Industry & pages. The industry and its blueprint are set by the
 * platform; the company admin decides which of the offered pages the team
 * sees in the sidebar.
 */
export default function IndustryPagesSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const { info, labels, refresh, set } = useWorkspace();
  const [hidden, setHidden] = useState<Set<PageKey>>(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // refresh() never rejects (it keeps the cached layout on failure), so "no info once it settles" is the failure case.
  const reload = useCallback(() => {
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [refresh]);
  useEffect(reload, [reload]);
  useEffect(() => { setHidden(new Set(info?.hiddenPages ?? [])); }, [info?.hiddenPages]);

  const offered = useMemo(
    () => CRM_PAGES.filter((p) => p.key !== 'dashboard' && info?.availablePages?.[p.key]),
    [info?.availablePages],
  );
  const groups = useMemo(() => {
    const out: Array<{ group: string; pages: typeof offered }> = [];
    for (const p of offered) {
      const g = p.group === 'Operations' ? labels.operations.plural : p.group;
      let row = out.find((x) => x.group === g);
      if (!row) { row = { group: g, pages: [] }; out.push(row); }
      row.pages.push(p);
    }
    return out;
  }, [offered, labels.operations.plural]);

  const saved = new Set(info?.hiddenPages ?? []);
  const dirty = hidden.size !== saved.size || Array.from(hidden).some((k) => !saved.has(k));
  const renamed = WORD_KEYS.filter((k) => {
    const page = WORD_PAGE[k];
    const onPage = !page || info?.availablePages?.[page];
    return onPage && (labels[k].plural !== LABEL_DEFAULTS[k].plural || labels[k].singular !== LABEL_DEFAULTS[k].singular);
  });

  const toggle = (k: PageKey) => setHidden((h) => { const n = new Set(h); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const save = async () => {
    setSaving(true);
    try {
      set(await settingsService.setHiddenPages(Array.from(hidden)));
      toast.success('Sidebar updated for your team');
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  };

  if (!info) {
    return (
      <SettingsPage {...HEADER}>
        <div className="py-7">
          {loading ? (
            <div className="space-y-4"><div className="skeleton h-32 rounded-2xl" /><div className="skeleton h-64 rounded-2xl" /></div>
          ) : (
            <LoadErrorState what="your industry settings" onRetry={reload} />
          )}
        </div>
      </SettingsPage>
    );
  }

  const Icon = industryIcon(info.industry);
  const industryLabel = industryOption(info.industry)?.label ?? 'Not set yet';

  return (
    <SettingsPage {...HEADER}>
      <SettingsSection
        title="Your industry"
        description={`Set by Slightor for ${info.companyName}. It decides which pages and words your CRM starts with.`}
        aside={<span className="inline-flex items-center gap-1.5 rounded-full bg-surface-alt px-2.5 py-1 text-2xs font-medium text-text-secondary"><Lock size={11} /> Contact support to change</span>}
      >
        <SettingsCard padded={false}>
          <div className="divide-y divide-border">
            <SettingsRow icon={<Icon size={16} strokeWidth={1.8} />} label="Industry" value={industryLabel} locked />
            <SettingsRow
              icon={<LayoutGrid size={16} />}
              label="Setup"
              hint="The pages and words that come with your industry."
              value={info.family ? `${info.family.name} setup` : 'Standard CRM'}
              locked
            />
          </div>
        </SettingsCard>
      </SettingsSection>

      {renamed.length > 0 && (
        <SettingsSection
          title="Words your CRM uses"
          description={`Part of your ${info.family?.name ?? 'industry'} setup. Only the names change — your data stays the same.`}
        >
          <SettingsCard padded={false}>
            <div className="@container">
              <ul className="grid gap-px bg-border @md:grid-cols-2 @md:[&>li:last-child:nth-child(odd)]:col-span-2">
                {renamed.map((k) => (
                  <li key={k} className="flex items-center gap-2.5 bg-surface px-5 py-3 text-sm">
                    <span className="min-w-0 truncate text-text-muted line-through decoration-text-muted/40">{LABEL_DEFAULTS[k].plural}</span>
                    <ArrowRight size={13} className="shrink-0 text-text-muted" aria-hidden />
                    <span className="min-w-0 truncate font-medium text-text-primary">{labels[k].plural}</span>
                  </li>
                ))}
              </ul>
            </div>
          </SettingsCard>
        </SettingsSection>
      )}

      <SettingsSection
        title="Pages in the sidebar"
        description={isAdmin
          ? 'Switch off the pages your team doesn’t use. Hidden pages keep their data and can be shown again any time.'
          : 'Only a company admin can change these.'}
        aside={offered.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-surface-alt px-2.5 py-1 text-2xs font-medium tabular-nums text-text-secondary">
            {offered.length - hidden.size} of {offered.length} shown
          </span>
        )}
      >
        <SettingsCard padded={false}>
          {offered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><LayoutGrid size={20} /></div>
              <div className="text-sm font-medium text-text-primary">No pages to choose from</div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {groups.map((g) => (
                <div key={g.group} className="px-5 py-4">
                  <div className="mb-2.5 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{g.group}</div>
                  <div className="@container">
                    <div className="grid gap-2 @md:grid-cols-2 @2xl:grid-cols-3">
                      {g.pages.map((p) => (
                        <PageToggle
                          key={p.key}
                          name={pageName(p.key, labels)}
                          shown={!hidden.has(p.key)}
                          disabled={!isAdmin}
                          onToggle={() => toggle(p.key)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SettingsCard>
      </SettingsSection>

      {/* The page list is long: a card footer would sit below the fold, so the first toggle's change went unnoticed. */}
      {isAdmin && dirty && (
        <SettingsSaveBar onDiscard={() => setHidden(new Set(info.hiddenPages ?? []))} onSave={save} saving={saving} />
      )}
    </SettingsPage>
  );
}

/** The whole tile is the hit area; the switch inside is the control screen readers announce. */
function PageToggle({ name, shown, disabled, onToggle }: { name: string; shown: boolean; disabled: boolean; onToggle: () => void }) {
  return (
    <label
      className={clsx(
        'flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-sm transition-colors',
        shown ? 'border-border bg-surface text-text-primary' : 'border-dashed border-border bg-surface-alt text-text-muted',
        disabled ? 'cursor-default' : 'cursor-pointer hover:border-border-strong',
      )}
    >
      <span className="min-w-0 truncate font-medium">{name}</span>
      <Switch checked={shown} onChange={onToggle} disabled={disabled} label={`Show ${name} in the sidebar`} />
    </label>
  );
}

/** A page's name in the company's words (Contacts → Guests). */
function pageName(key: PageKey, labels: ReturnType<typeof useWorkspace>['labels']): string {
  const word = (Object.entries(WORD_PAGE) as Array<[LabelKey, PageKey]>).find(([, p]) => p === key)?.[0];
  if (word) return labels[word].plural;
  return CRM_PAGES.find((p) => p.key === key)?.label ?? key;
}
